import { useUiMode } from "@/lib/uiMode";
import { useViewport } from "./ViewportProvider";

/**
 * One address, up to three renderings.
 *
 * The route table is the same on every surface — `router.tsx` builds it once, from the same
 * descriptor list the rail renders — and this is where a route decides which of its screens
 * is on screen. Doing it here rather than with a second router is what keeps a link, a
 * manifest shortcut, a chat citation and a deep link meaning exactly one thing.
 *
 * The order the three are asked in is a decision, not an accident. **The phone wins first**:
 * the phone app is already the simple-mode surface plus People (M12), so a phone in simple
 * mode is the phone app rather than a 760 px column squeezed into 390. Then simple mode,
 * then the desktop screen the route was written for first.
 *
 * A route with no phone screen passes `<DesktopOnly/>`, and a route with no simple screen
 * passes none at all and falls through to its desktop one — which is right for the screens
 * simple mode merely *reads* the same way (Search), and wrong for nothing, because the
 * destinations simple mode does not want are absent from its rail rather than reduced.
 */
export function Surface({
  desktop,
  phone,
  simple,
}: {
  desktop: React.ReactNode;
  phone: React.ReactNode;
  /** UI-16's rendering of this address, where it has one. */
  simple?: React.ReactNode;
}) {
  const { viewport } = useViewport();
  const mode = useUiMode();

  if (viewport === "phone") return <>{phone}</>;
  if (mode === "simple" && simple !== undefined) return <>{simple}</>;

  return <>{desktop}</>;
}
